import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';

// Axios base configuration
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'https://gokturkgpt-api.gokturkgpt.workers.dev';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add JWT token to requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchChats = async () => {
    try {
      const response = await axios.get('/api/chats');
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  };

  const createChat = async (title = 'Yeni Sohbet') => {
    try {
      const response = await axios.post('/api/chats', { title });
      const newChat = response.data;
      setChats(prev => [newChat, ...prev]);
      return newChat;
    } catch (error) {
      console.error('Error creating chat:', error);
      throw error;
    }
  };

  const deleteChat = async (chatId) => {
    try {
      const response = await axios.delete(`/api/chats/${chatId}`);
      console.log('Delete chat response:', response.data);
      
      setChats(prev => prev.filter(chat => chat.id !== chatId));
      if (currentChat?.id === chatId) {
        setCurrentChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Sohbet silinemedi';
      throw new Error(errorMessage);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/chats/${chatId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (chatId, message, modelType = 'free') => {
    if (!chatId) return;

    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      message_type: 'text'
    };

    try {
      setLoading(true);
      setMessages(prev => [...prev, userMessage]);

      const response = await axios.post(`/api/chats/${chatId}/messages`, {
        message,
        model_type: modelType
      });

      const { ai_response } = response.data;
      setMessages(prev => [...prev, ai_response]);
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the user message if sending failed
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async (prompt) => {
    try {
      const response = await axios.post('/api/generate-image', { prompt });
      return response.data;
    } catch (error) {
      console.error('Error generating image:', error);
      throw error;
    }
  };

  const generateVideo = async (prompt) => {
    try {
      const response = await axios.post('/api/generate-video', { prompt });
      return response.data;
    } catch (error) {
      console.error('Error generating video:', error);
      throw error;
    }
  };

  const value = {
    chats,
    currentChat,
    messages,
    loading,
    setCurrentChat,
    setMessages,
    fetchChats,
    createChat,
    deleteChat,
    fetchMessages,
    sendMessage,
    generateImage,
    generateVideo
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
